import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import { addAllList } from 'modules/common';
import { getDate } from 'lib/commonLib';
import { rtbPlatformList } from 'constants/resources/commonCustomize';

import Icon from 'components/atoms/Icon';
import MbButton from 'components/atoms/MbButton';
import MbTooltip from 'components/atoms/MbTooltip';
import NewSizeTypeFilter from 'components/molecules/Filter/NewSizeTypeFilter';
import TimeFilter from 'components/organisms/TimeFilter';
import SingleDate from 'components/organisms/DatePicker/SingleDate';
import NewDisplayFilter from 'components/organisms/NewDisplayFilter';
import NewManagerFilter from 'components/organisms/NewManagerFilter';
import FilterCollection from 'components/organisms/FilterCollection';
import NewExternalFilter from 'components/organisms/NewExternalFilter';
import MediaTpCode from 'components/organisms/NewCommonFilter/MediaTpCode';
import DateRangePicker from 'components/organisms/DatePicker/DateRangePicker';

const NewCommonFilter = React.memo((props) => {
  const { t } = useTranslation();
  const {
    initialization,
    getParam,
    params,
    displayFilterList,
    toolTip,
    // 필터 사용 유무 설정
    usePltfomTpCodeFilter = true,
    useOpenRtbFilter = false,
    excludeAllPltfomTpCode = false,
    useDate = true,
    useDisplayFilter = true,
    useExternalFilter = true,
    useDiffDate = false,
    useSingleDate = false,
    useMonthDate = false,
    useMediaTpCode = false,
    useTimeFilter = false,
    useManagerFilter = false,
    useSizeType = false,
    useWeekFilter = false,
    productTargeting = false,
    disabled = false,
    // 필터 옵션
    pltfomTpCodeDetailFilter = [], // 유형구분 추가 필터 리스트
    pltfomTpCodeFixed = [], // 원하는 플렛폼코드만 사용
    dateLabel = t('날짜'),
    diffDateLabel = t('비교날짜'),
    singleDateId = 'statsDttm',
    useDatePresets = true,
    timeFilterType,
    required,
    selectOnlyOne = {},
    externalList,
    useTracker = false,
    useTkgTpCode = false,
    useDisplayAll = true,
    onlyProducts = false,
    onlyTargeting = false,
    customPresets = [],
    diffCustomPresets = [],
    externalType = 'checkbox',
    children,
  } = props;
  const { platformCodeList } = useSelector(({ common }) => ({
    platformCodeList: common.platformCodeList,
  }));
  const now = moment();

  const [filterData, setFilterData] = useState({});
  const [filterList, setFilterList] = useState({
    platformCodeList: [],
    trackerList: [
      { label: t('포함'), value: 'all' },
      { label: t('제외'), value: '01' },
      { label: t('전용'), value: '02' },
    ],
    weekList: [
      { label: t('전체'), value: 'all' },
      { label: t('평일'), value: '01' },
      { label: t('주말'), value: '02' },
    ],
    tkgTpCodeList: [
      { label: t('전체'), value: 'all' },
      { label: t('연동'), value: 'Y' },
      { label: t('미연동'), value: 'N' },
    ]
  });

  useEffect(() => {
    handleInitParams();
    handleGetList();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (initialization === true) {
      handleInitParams();
    }
    handleGetList();
    // eslint-disable-next-line
  }, [params]);

  /**
   * list state 값이 바뀌었을 때 넣어주는 함수
   */
  useEffect(() => {
    handleGetList();
    // eslint-disable-next-line
  }, [platformCodeList]);

  const handleGetList = () => {
    let newFilterList = filterList;

    if (usePltfomTpCodeFilter) {
      // 유형구분 관련 초기값 설정 및 커스텀 옵션 추가
      if (excludeAllPltfomTpCode) {
        newFilterList = {
          ...newFilterList,
          platformCodeList: platformCodeList,
        };
      } else {
        newFilterList = {
          ...newFilterList,
          platformCodeList: addAllList(
            platformCodeList,
            'label',
            'value',
            'all'
          ),
        };
      }

      // 유형구분 추가 필터 사용여부에
      let temPpltfomTpCodeFixed = [...pltfomTpCodeFixed];

      if (pltfomTpCodeDetailFilter.length > 0 || pltfomTpCodeFixed.length > 0) {
        if (pltfomTpCodeFixed.length > 0) {
          if (pltfomTpCodeFixed.includes('00')) {
            newFilterList.platformCodeList = [
              { label: t('전체'), value: 'all' },
            ];
            temPpltfomTpCodeFixed.splice(0, 1);
          } else {
            newFilterList.platformCodeList = [];
          }
        }
        const pltCodeList =
          pltfomTpCodeDetailFilter.length > 0
            ? pltfomTpCodeDetailFilter
            : temPpltfomTpCodeFixed;
        for (let pltfomTpCode of pltCodeList) {
          let str = '';
          switch (pltfomTpCode) {
            case '01':
              str = '온라인';
              break;
            case '02':
              str = '모바일';
              break;
            case '03':
              str = '모바일웹';
              break;
            case '04':
              str = 'APP';
              break;
            case '05':
              str = '미승인';
              break;
            default:
              str = 'pltfomTpCode ' + pltfomTpCode + ' 알수없음';
          }
          newFilterList.platformCodeList.push({
            label: t(str),
            value: pltfomTpCode,
          });
        }
      }
    }

    // 최종적으로 list를 업데이트 시킴.
    setFilterList(newFilterList);
  };

  const handleInitParams = () => {
    let tempInitParams = { ...params };

    // 날짜 기본값 설정
    if (useDate === true && useDiffDate === false && useSingleDate === false) {
      let sDate = moment(now.format('YYYY-MM-01'));
      let eDate = now;

      if (typeof tempInitParams.sDate !== 'undefined') {
        sDate = moment(tempInitParams.sDate, 'YYYYMMDD');
      }
      if (typeof tempInitParams.eDate !== 'undefined') {
        eDate = moment(tempInitParams.eDate, 'YYYYMMDD');
      }

      // const query = queryString.parse(location.search);
      //
      // if (typeof query.sDate !== 'undefined') {
      //   sDate = moment(query.sDate, 'YYYYMMDD');
      // }
      // if (typeof query.eDate !== 'undefined') {
      //   eDate = moment(query.eDate, 'YYYYMMDD');
      // }
      setFilterData({
        ...filterData,
        sDate: sDate,
        eDate: eDate,
      });
      if (useMonthDate) {
        tempInitParams = {
          ...tempInitParams,
          sMth: params.sMth,
          eMth: params.eMth,
        };
      } else {
        tempInitParams = {
          ...tempInitParams,
          sDate: getDate(sDate),
          eDate: getDate(eDate),
        };
      }
    } else if (useDate === true && useDiffDate && useSingleDate === false) {
      let diffSDate = moment().add(-1, 'day');
      let diffEDate = moment().add(-1, 'day');
      let sDate = moment().add(-2, 'day');
      let eDate = moment().add(-2, 'day');

      if (typeof tempInitParams.sDate !== 'undefined') {
        sDate = moment(tempInitParams.sDate, 'YYYYMMDD');
      }
      if (typeof tempInitParams.eDate !== 'undefined') {
        eDate = moment(tempInitParams.eDate, 'YYYYMMDD');
      }
      if (typeof tempInitParams.diffSDate !== 'undefined') {
        diffSDate = moment(tempInitParams.diffSDate, 'YYYYMMDD');
      }
      if (typeof tempInitParams.diffEDate !== 'undefined') {
        diffEDate = moment(tempInitParams.diffEDate, 'YYYYMMDD');
      }
      setFilterData({
        ...filterData,
        sDate: sDate,
        eDate: eDate,
        diffSDate: diffSDate,
        diffEDate: diffEDate,
      });
      tempInitParams = {
        ...tempInitParams,
        sDate: getDate(sDate),
        eDate: getDate(eDate),
        diffSDate: getDate(diffSDate),
        diffEDate: getDate(diffEDate),
      };
    } else if (useSingleDate) {
      if (!useMonthDate) {
        if (typeof tempInitParams[singleDateId] !== 'undefined') {
          const tempSingleDate = moment(
            tempInitParams[singleDateId],
            'YYYYMMDD'
          );
          setFilterData({
            ...filterData,
            [singleDateId]: tempSingleDate,
          });
          tempInitParams = {
            ...tempInitParams,
            [singleDateId]: getDate(tempSingleDate),
          };
        }
      } else {
        // let statsMth = moment().add(-1, 'months').format('YYYYMM');
        // let statsMth = moment().date(0);
        //
        // if (typeof tempInitParams.statsMth !== 'undefined') {
        //   statsMth = moment(tempInitParams.statsMth, 'YYYYMM');
        // }
        //
        // setFilterData({
        //   ...filterData,
        //   statsMth: statsMth,
        // });
        // tempInitParams = {
        //   ...tempInitParams,
        //   statsMth: getMonthDate(statsMth),
        // };
      }
    }

    // 외부연동 기본값 설정
    // if (useExternalFilter && itlTpCode === null) {

    // 유형구분 값이 없을때 기본값 설정
    if (
      (usePltfomTpCodeFilter &&
        typeof tempInitParams.pltfomTpCode === 'undefined') ||
      tempInitParams.pltfomTpCode === null
    ) {
      if (excludeAllPltfomTpCode) {
        tempInitParams = {
          ...tempInitParams,
          pltfomTpCode: '01',
        };
      } else {
        tempInitParams = {
          ...tempInitParams,
          pltfomTpCode: 'all',
        };
      }
    }

    // 디스플레이 기본값 설정
    if (useDisplayFilter) {
    }

    // 사이즈형태 기본값 설정
    if (useSizeType) {
      if (typeof params.sizeType === 'undefined') {
        tempInitParams = {
          ...tempInitParams,
          sizeType: 'all',
        };
      }
    }

    // 매체 카테고리 기본값 설정
    if (useMediaTpCode) {
      if (
        typeof tempInitParams.crgrNo === 'undefined' ||
        tempInitParams.crgrNo === null
      ) {
        tempInitParams = {
          ...tempInitParams,
          crgrNo: 'all',
        };
      }
    }

    // 영업 담당자 기본값 설정
    if (useManagerFilter) {
      if (
        typeof tempInitParams.contact === 'undefined' ||
        tempInitParams.contact === null
      ) {
        tempInitParams = {
          ...tempInitParams,
          contact: 'all',
        };
      }
    }
    getParam(tempInitParams);
  };

  const handleGetParams = (newParams) => {
    getParam(newParams);
  };

  /**
   * 날짜 필터
   */
  const handleGetDate = (newDate) => {
    const { startDate, endDate } = newDate;
    let newParams = { ...params };
    if (startDate !== null && endDate !== null) {
      if (useMonthDate) {
        newParams = {
          ...newParams,
          sMth: startDate,
          eMth: endDate,
        };
      } else {
        newParams = {
          ...newParams,
          sDate: startDate,
          eDate: endDate,
        };
      }
    }
    getParam({ ...newParams });
  };

  /**
   * 비교날짜 필터
   */
  const handleOnchangeDiffDate = (newDate) => {
    const { startDate, endDate } = newDate;
    let newParams = { ...params };
    if (startDate !== null && endDate !== null) {
      newParams = {
        ...newParams,
        diffSDate: startDate,
        diffEDate: endDate,
      };
    }
    getParam({ ...newParams });
  };

  /**
   * 기준날짜
   */
  const handleSingleDate = (newDate) => {
    getParam({ ...params, ...newDate });
  };

  const handleProductTargeting = () => {
    const newParams = {
      ...params,
      advrtsTpCode: '04,10,16,17,34',
      advrtsPrdtCode: '01',
    };
    getParam({ ...newParams });
  };

  return (
    <>
      {toolTip !== null && toolTip !== undefined ? (
        <>
          <MbTooltip title={toolTip} placement="top-start">
            <Icon icon={'faInfoCircle'} color="#c5c6d0" />
          </MbTooltip>
        </>
      ) : null}
      {(useDate === true &&
        typeof params.sDate !== 'undefined' &&
        typeof params.eDate !== 'undefined') ||
      (useMonthDate === true &&
        typeof params.sMth !== 'undefined' &&
        typeof params.eMth !== 'undefined') ? (
        <DateRangePicker
          startDateId="startDate"
          endDateId="endDate"
          handleOnChange={handleGetDate}
          initialStartDate={
            useMonthDate === false
              ? moment(params.sDate, 'YYYYMMDD')
              : moment(params.sMth, 'YYYYMMDD')
          }
          initialEndDate={
            useMonthDate === false
              ? moment(params.eDate, 'YYYYMMDD')
              : moment(params.eMth, 'YYYYMMDD')
          }
          label={dateLabel}
          filtered
          useMonthDate={useMonthDate}
          useDatePresets={useDatePresets}
          customPresets={customPresets}
          disabled={disabled}
        />
      ) : null}
      {useDiffDate &&
        typeof params.diffSDate !== 'undefined' &&
        typeof params.diffEDate !== 'undefined' && (
          <DateRangePicker
            startDateId="diffStartDate"
            endDateId="diffEndDate"
            handleOnChange={handleOnchangeDiffDate}
            initialStartDate={moment(params.diffSDate, 'YYYYMMDD')}
            initialEndDate={moment(params.diffEDate, 'YYYYMMDD')}
            label={diffDateLabel}
            filtered
            customPresets={diffCustomPresets}
            useDatePresets={diffCustomPresets.length > 0}
            disabled={disabled}
          />
        )}
      {useSingleDate && typeof params[singleDateId] !== 'undefined' && (
        <SingleDate
          id={singleDateId}
          handleOnChange={handleSingleDate}
          initialDate={
            useMonthDate === false
              ? moment(params[singleDateId], 'YYYYMMDD')
              : moment(params.statsMth, 'YYYYMMDD')
          }
          label={t('기준날짜')}
          useMonthDate={useMonthDate}
        />
      )}
      {children}
      {filterList.platformCodeList.length > 0 && usePltfomTpCodeFilter && (
        <FilterCollection
          label={t('유형 구분')}
          name="pltfomTpCode"
          params={params}
          value={params.pltfomTpCode}
          type="radio"
          list={filterList.platformCodeList}
          callBack={handleGetParams}
          labelKey="label"
          valueKey="value"
        />
      )}
      {!usePltfomTpCodeFilter && useOpenRtbFilter && (
        <FilterCollection
          label={t('유형 구분')}
          name="pltfomTpCode"
          params={params}
          value={params.pltfomTpCode}
          type="radio"
          list={rtbPlatformList}
          callBack={handleGetParams}
          labelKey="label"
          valueKey="value"
        />
      )}
      {useDisplayFilter && (
        <NewDisplayFilter
          handleOnClickFilter={handleGetParams}
          advrtsPrdtCode={params.advrtsPrdtCode}
          displayFilterList={displayFilterList}
          params={params}
          required={
            typeof required !== 'undefined'
              ? required.type === 'DisplayFilter'
                ? required.val
                : null
              : null
          }
          selectOnlyOne={selectOnlyOne.displayFilter === true}
          useDisplayAll={useDisplayAll}
          onlyProducts={onlyProducts}
          onlyTargeting={onlyTargeting}
        />
      )}
      {useExternalFilter && (
        <NewExternalFilter
          params={params}
          handleGetParams={handleGetParams}
          name="itlTpCode"
          externalList={externalList}
          type={externalType}
        />
      )}
      {useTimeFilter && (
        <TimeFilter
          id="mb-daily-statistics-time-popper"
          getParam={getParam}
          params={params}
          useRange={
            typeof timeFilterType !== 'undefined' && timeFilterType.useRange
          }
        />
      )}
      {useManagerFilter && (
        <NewManagerFilter
          label={t('영업담당자')}
          name="contact"
          params={params}
          getParam={getParam}
        />
      )}
      {useSizeType && (
        <NewSizeTypeFilter
          label={t('사이즈 형태')}
          name="sizeType"
          optionLabelKey="CODE_DESC"
          optionValueKey="CODE_ID"
          getParam={getParam}
          params={params}
        />
      )}
      {useWeekFilter && (
        <FilterCollection
          label={t('요일구분')}
          name="weekFlag"
          params={params}
          value={params.weekFlag}
          type="radio"
          list={filterList.weekList}
          callBack={handleGetParams}
          labelKey="label"
          valueKey="value"
        />
      )}
      {useMediaTpCode && (
        <MediaTpCode
          params={params}
          value={params.crgrNo}
          callBack={handleGetParams}
        />
      )}
      {useTracker && (
        <FilterCollection
          label={t('앱트래커')}
          name="trackerFlag"
          params={params}
          value={params.trackerFlag}
          type="radio"
          list={filterList.trackerList}
          callBack={handleGetParams}
          labelKey="label"
          valueKey="value"
        />
      )}
      {useTkgTpCode && (
        <FilterCollection
          label={t('포스트백')}
          name="tkgTpCode"
          params={params}
          value={params.tkgTpCode}
          type="radio"
          list={filterList.tkgTpCodeList}
          callBack={handleGetParams}
          labelKey="label"
          valueKey="value"
      />
      )}
      {productTargeting && (
        <MbButton filterButton clickEvent={handleProductTargeting}>
          상품타게팅(SR+CW+RC+SP+PR)
        </MbButton>
      )}
    </>
  );
});
NewCommonFilter.propTypes = {
  initialization: PropTypes.bool.isRequired,
  getParam: PropTypes.func.isRequired,
  params: PropTypes.object.isRequired,
  toolTip: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  displayFilterList: PropTypes.object,
  usePltfomTpCodeFilter: PropTypes.bool,
  excludeAllPltfomTpCode: PropTypes.bool,
  useDate: PropTypes.bool,
  useDisplayFilter: PropTypes.bool,
  useExternalFilter: PropTypes.bool,
  useDiffDate: PropTypes.bool,
  useSingleDate: PropTypes.bool,
  useMonthDate: PropTypes.bool,
  useMediaTpCode: PropTypes.bool,
  useTimeFilter: PropTypes.bool,
  useManagerFilter: PropTypes.bool,
  useSizeType: PropTypes.bool,
  pltfomTpCodeDetailFilter: PropTypes.array,
  required: PropTypes.object,
  // productTargeting: PropTypes.bool, 해당 페이지로 빼기
  // timeFilterType: PropTypes.bool, 쓰는곳이 없는것같음..
};
export default withRouter(NewCommonFilter);
